import type React from 'react';
import { useCallback, useEffect, useState } from 'react';

import { Dialog } from '@headlessui/react';
import { IoMdClose } from 'react-icons/io';

import { isNil } from 'lodash-es';
import Button from '../button/Button';

interface ModalProps {
  id: string;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  onClose: () => void;
  onSubmit?: () => void;
  title?: string;
  body?: React.ReactElement;
  footer?: React.ReactElement;
  actionLabel?: string;
  actionType?: string;
  disabled?: boolean;
  loading?: boolean;
  secondaryAction?: () => void;
  secondaryActionLabel?: string;
  secondaryActionDisabled?: boolean;
  secondaryActionLoading?: boolean;
  noFit?: boolean;
  isAction?: boolean;
  isInfo?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  id,
  isOpen,
  setIsOpen,
  onClose,
  onSubmit,
  title,
  body,
  footer,
  actionLabel,
  actionType,
  disabled,
  loading,
  secondaryAction,
  secondaryActionLabel,
  secondaryActionDisabled,
  secondaryActionLoading,
  noFit,
  isAction,
  isInfo,
}) => {
  const [showModal, setShowModal] = useState(isOpen);

  useEffect(() => {
    setShowModal(isOpen);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setShowModal(false);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(() => {
    if (disabled) {
      return;
    }

    if (!isNil(onSubmit)) {
      onSubmit();
    }
  }, [disabled, onSubmit]);

  const handleSecondaryAction = useCallback(() => {
    if (secondaryActionDisabled || !secondaryAction) {
      return;
    }

    secondaryAction();
  }, [secondaryActionDisabled, secondaryAction]);

  return (
    <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
      <div
        data-test-id="modal-external-fill"
        className="fixed inset-0 size-full bg-neutral-800/70 z-40 backdrop-blur-sm"
      >
        &nbsp;
      </div>
      {/** CONTENT */}
      <div
        data-test-id="modal-external-fill"
        className="fixed inset-0 z-40 size-full overflow-y-auto overflow-x-hidden outline-none focus:outline-none"
      >
        <div
          className={`relative flex items-center justify-center transition duration-200 ${
            showModal ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div
            data-test-id="modal-dialog-container"
            className="z-50 grid min-h-[calc(100dvh)] w-full max-w-screen-sm grid-cols-1"
          >
            <div className="flex max-h-[calc(100dvh)] flex-col items-center justify-center p-1">
              <div
                className={`flex ${
                  noFit ? '' : 'h-full'
                } max-h-[840px] min-w-full flex-col rounded-lg border-0 bg-white shadow-lg outline-none transition focus:outline-none`}
              >
                {/** HEADER */}
                {!isAction && (
                  <div
                    className={`flex flex-row items-center justify-between rounded-t ${isInfo ? 'pt-2 px-2' : 'border-b py-4 px-6'}`}
                  >
                    <div className="opacity-0">
                      <IoMdClose size={18} />
                    </div>
                    <div className="text-lg font-semibold">{title}</div>
                    <button
                      aria-label="modal-button-close"
                      id="modal-button-close"
                      key="modal-button-close"
                      data-test-id="modal-button-close"
                      type="button"
                      className="left-9 border-0 p-1 outline-none transition hover:opacity-70"
                      onClick={handleClose}
                    >
                      <IoMdClose size={18} />
                    </button>
                  </div>
                )}
                {/** BODY */}
                <div
                  className={`flex min-w-full grow flex-col overflow-y-auto overflow-x-hidden px-6 ${!isInfo ? 'pt-6' : 'pt-2'}`}
                >
                  {body}
                </div>
                {/** FOOTER */}
                <div className="m-3 mx-6 flex flex-col gap-2">
                  <div className="flex size-full flex-col items-center gap-2 sm:flex-row sm:gap-4">
                    {secondaryAction && secondaryActionLabel && (
                      <Button
                        disabled={secondaryActionDisabled}
                        loading={secondaryActionLoading}
                        label={secondaryActionLabel}
                        onClick={handleSecondaryAction}
                        data-test-id={`${id}_modal_secondary-action`}
                        id={`${id}_modal_secondary-action`}
                        outline
                      />
                    )}
                    {onSubmit && actionLabel && (
                      <Button
                        disabled={disabled}
                        loading={loading}
                        label={actionLabel}
                        onClick={handleSubmit}
                        data-test-id={`${id}_modal_primary-action`}
                        id={`${id}_modal_primary-action`}
                        reject={actionType === 'reject'}
                        accept={actionType === 'accept'}
                        outline={actionType === 'neutral'}
                      />
                    )}
                  </div>
                  {footer}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default Modal;
